// Own App's modal slots and completion callbacks together. Cancelling a session
// rejects the queue's completions; it never synthesizes a successful modal result.
export function createSessionModalQueue({ slots, security, overlayMode, deferred, schedule, cancelSchedule, afterUnmount }) {
    const entries = new Set(), queue = [];
    let generation = 0;
    const cancelled = () => ({ cancelled: true, sessionChanged: true });
    function current(entry) {
        return !entry.settled && entry.generation === generation && security.incarnationMatches(entry.owner);
    }
    function releaseSlot(entry) {
        const slot = slots[entry.slot];
        if (slot?.entry !== entry) return;
        slot.entry = null;
        slot.releasing = true;
        slot.type.value = null;
        slot.props.value = null;
        afterUnmount(() => { slot.releasing = false; drain(); });
    }
    function settle(entry, success, result) {
        if (entry.settled) return;
        const admitted = current(entry);
        entry.settled = true;
        cancelSchedule(entry.timer);
        entry.release();
        for (const stop of [...entry.subscriptions]) stop();
        entries.delete(entry);
        releaseSlot(entry);
        if (success && admitted) {
            entry.completion.resolve(result);
            const callback = entry.data.callback?.();
            if (entry.generation === generation && security.incarnationMatches(entry.owner)) callback?.(result);
        } else entry.completion.reject(result || cancelled());
        afterUnmount(drain);
    }
    function cancelEntry(entry) {
        if (entry.settled) return;
        const component = slots[entry.slot]?.entry === entry ? slots[entry.slot].component.value : null;
        // Fence callbacks before a component's optional cancellation hook settles.
        settle(entry, false, cancelled());
        component?.cancelForRecovery?.();
    }
    function mount(entry, index) {
        if (!current(entry)) { cancelEntry(entry); return; }
        const slot = slots[index];
        entry.slot = index;
        slot.entry = entry;
        // Modal-specific callback props share the same admission as App's result.
        const props = { ...entry.data.data };
        for (const [key, value] of Object.entries(props)) {
            if (typeof value !== 'function') continue;
            if (/^subscribe[A-Z]/.test(key)) {
                // Receivers belong to the entry; unsubscribe must still work
                // after cancellation, including synchronous subscription delivery.
                props[key] = function (receive, ...args) {
                    if (!current(entry)) return () => {};
                    const release = value.call(this, (...values) => { if (current(entry)) receive(...values); }, ...args);
                    let stopped = false;
                    const stop = () => {
                        if (stopped) return;
                        stopped = true;
                        entry.subscriptions.delete(stop);
                        release?.();
                    };
                    if (current(entry)) entry.subscriptions.add(stop); else stop();
                    return stop;
                };
            } else if (!/^(get|is|has)[A-Z]/.test(key)) {
                // Function props are actions by default (including dataManagerCallback,
                // operateSignup/Transfer, response handlers and sharing actions).
                // Pure getter/configuration contracts retain their identity.
                props[key] = function (...args) { if (current(entry)) return value.apply(this, args); };
            }
        }
        slot.props.value = props;
        slot.type.value = entry.data.type;
        entry.timer = schedule(() => {
            if (!current(entry) || slot.entry !== entry) return;
            const component = slot.component.value;
            if (!component?.show) { cancelEntry(entry); return; }
            component.show().then(result => settle(entry, true, result), error => settle(entry, false, error));
        });
    }
    function drain() {
        while (queue.length && !current(queue[0])) cancelEntry(queue.shift());
        if (queue.length && !slots[0].entry && !slots[0].releasing) mount(queue.shift(), 0);
    }
    function show(data) {
        const entry = { data, owner: security.getIncarnation(), generation, settled: false, completion: deferred(), release: () => {}, subscriptions: new Set(), slot: -1 };
        data.cancel = () => cancelEntry(entry);
        entries.add(entry);
        entry.release = security.registerCancellation(() => cancelEntry(entry));
        const overlay = data.displayMode === overlayMode;
        const free = slot => !slot.entry && !slot.releasing;
        const index = overlay ? slots.findIndex(free) : (free(slots[0]) ? 0 : -1);
        if (index < 0) queue.push(entry); else mount(entry, index);
        return entry.completion.promise();
    }
    function cancel() {
        generation++;
        queue.length = 0;
        for (const entry of [...entries]) cancelEntry(entry);
    }
    return { show, cancel };
}
